import { defineStore } from "pinia";
import { computed, effectScope, ref, watch } from "vue";
import {
	acceptInboxInvitation,
	declineInboxInvitation,
	getInbox,
	type AcceptInvitationResponse,
	type InboxInvitation,
} from "@/api";
import { onResync, onSocketEvent } from "@/socket";
import { useAuth } from "@/shared/composables/useAuth";
import { errorMessage } from "@/shared/utils/errors";
import { inboxFailure, type InboxFailure } from "@/shared/utils/invitations";

/** What an event of the user's room can make stale: the one list this store keeps. */
export type InboxSyncTarget = "inbox";

/**
 * The events of the user's own room that concern the invitations inbox, and what each makes stale —
 * checked against `UserEvent` in `server/src/socket/broadcast.rs` by `npm run lint:sync`, which
 * reads this registry with the other user-room ones.
 *
 * `invitation:received` is one arriving (any of the three kinds, so one registry covers them, and
 * the Designer's project list no longer hears it: an invitation is not a membership, the project
 * is listed once it is accepted, which `project:membership:changed` tells). `invitations:changed`
 * is the pending set changing some other way: it was accepted or declined (in another tab — and
 * this one, which refetches what it has just changed), or its inviter revoked it or sent it again
 * with a new link. Both go only to a user whose provider has verified the address the invitation
 * is for, which is the inbox's own rule.
 *
 * The events name no row and say nothing of what the list now is, so the list is read again.
 */
export const INVITATION_EVENT_HANDLERS: Record<string, readonly InboxSyncTarget[]> = {
	"invitation:received": ["inbox"],
	"invitations:changed": ["inbox"],
};

/** The outcome of answering an invitation: what it returned, or why not and whether to read again. */
export type InboxAnswer<T> = { ok: true; value: T } | { ok: false; failure: InboxFailure };

/**
 * The signed-in user's invitations inbox — what has been sent to the email their provider has
 * verified, of every kind — and the one count the user menu's badge and the home pages' banners
 * show. One store for all of them, read once after sign-in and kept current by the user's room
 * (`wireInvitations`), so the badge on the avatar and the Account section cannot disagree.
 *
 * Only the newest read lands: a slow answer to an older one never replaces a newer list, nor an
 * invitation this session has just answered. A read that fails keeps the list it has — the list is
 * not wrong, only not newer — and says so in `error`.
 */
export const useInvitationsStore = defineStore("invitations", () => {
	const invitations = ref<InboxInvitation[]>([]);
	/** `null` until the first read: whether the provider has verified the account's email. */
	const emailVerified = ref<boolean | null>(null);
	const loading = ref(false);
	const error = ref("");
	/**
	 * How many were pending when the home pages' banner was dismissed. In memory only, so it ends with
	 * the page load and nothing is written to the device; the banner comes back when there are more.
	 */
	const dismissedCount = ref(0);

	let generation = 0;

	const loaded = computed(() => emailVerified.value !== null);
	/** Pending invitations — none while the provider has not verified the email, whatever was sent. */
	const count = computed(() => (emailVerified.value ? invitations.value.length : 0));
	const bannerVisible = computed(() => count.value > 0 && count.value > dismissedCount.value);

	/** Reads the inbox again, whatever is held. */
	async function reload(): Promise<void> {
		const mine = ++generation;
		loading.value = true;
		try {
			const inbox = await getInbox();
			if (mine !== generation) return;
			invitations.value = inbox.invitations;
			emailVerified.value = inbox.email_verified;
			error.value = "";
		} catch (e) {
			if (mine === generation) error.value = errorMessage(e);
		} finally {
			if (mine === generation) loading.value = false;
		}
	}

	/** Reads the inbox unless it has been read: what a page that shows it asks for. */
	function load(): Promise<void> {
		return loaded.value ? Promise.resolve() : reload();
	}

	/**
	 * What an event of the user's room made stale. An event of the room that is not registered here
	 * is none of this store's business.
	 */
	function applyRemote(event: string): Promise<void> {
		return INVITATION_EVENT_HANDLERS[event]?.includes("inbox") ? reload() : Promise.resolve();
	}

	/** Takes `invitation` off the list: it was answered, and nothing more is known until the next read. */
	function drop(invitation: Pick<InboxInvitation, "kind" | "id">) {
		invitations.value = invitations.value.filter(
			(i) => !(i.kind === invitation.kind && i.id === invitation.id),
		);
	}

	/**
	 * Answers an invitation, and takes it off the list when the server took the answer. A refusal that
	 * says the list is out of date (the invitation is gone, or the account's email is not verified
	 * after all) reads it again; one that does not (a personal space with no room: the invitation stays
	 * pending) leaves it alone.
	 */
	async function answer<T>(
		invitation: Pick<InboxInvitation, "kind" | "id">,
		send: () => Promise<T>,
	): Promise<InboxAnswer<T>> {
		try {
			const value = await send();
			drop(invitation);
			return { ok: true, value };
		} catch (e) {
			const failure = inboxFailure(e);
			if (failure.stale) void reload();
			return { ok: false, failure };
		}
	}

	/** Accepts: the caller becomes a member, as by the mailed link. */
	function accept(
		invitation: Pick<InboxInvitation, "kind" | "id">,
	): Promise<InboxAnswer<AcceptInvitationResponse>> {
		return answer(invitation, () => acceptInboxInvitation(invitation.kind, invitation.id));
	}

	/** Declines: the inviter's list shows it as declined. */
	function decline(invitation: Pick<InboxInvitation, "kind" | "id">): Promise<InboxAnswer<void>> {
		return answer(invitation, () => declineInboxInvitation(invitation.kind, invitation.id));
	}

	/** Hides the home pages' banner until there are more invitations than there are now. */
	function dismissBanner() {
		dismissedCount.value = count.value;
	}

	/** Forgets everything, for whoever signs in next, and drops any read still in flight. */
	function reset() {
		generation++;
		invitations.value = [];
		emailVerified.value = null;
		loading.value = false;
		error.value = "";
		dismissedCount.value = 0;
	}

	return {
		invitations,
		emailVerified,
		loading,
		error,
		loaded,
		count,
		bannerVisible,
		load,
		reload,
		applyRemote,
		accept,
		decline,
		dismissBanner,
		reset,
	};
});

/**
 * Wires the store to the session and the user's room, once per app: it is read when somebody signs
 * in and forgotten when they sign out, re-read for each event of `INVITATION_EVENT_HANDLERS` and for
 * the room being read afresh (a reconnect, a missed number). `onSocketEvent` handlers live as long
 * as the page does, and the watcher is in a scope of its own so the component that happened to call
 * this first unmounting does not stop it. Called by `useInvitations`.
 */
const wired = new WeakSet<object>();
export function wireInvitations(store: ReturnType<typeof useInvitationsStore>): void {
	if (wired.has(store)) return;
	wired.add(store);
	for (const event of Object.keys(INVITATION_EVENT_HANDLERS)) {
		onSocketEvent(event, () => void store.applyRemote(event));
	}
	onResync((room) => {
		if (room.startsWith("user:") && store.loaded) void store.reload();
	});
	const { isAuthenticated } = useAuth();
	effectScope(true).run(() => {
		watch(
			isAuthenticated,
			(signedIn) => {
				if (signedIn) void store.load();
				else store.reset();
			},
			{ immediate: true },
		);
	});
}
